import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Typography,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  TablePagination,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import SendIcon from "@material-ui/icons/Send";
import InfoIcon from "@material-ui/icons/Info";
import Close from "@material-ui/icons/Close";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { withStyles } from "@material-ui/core/styles";

import styles from "./codeListStyles";
import NewCodeDialog from "./newCodeDialog/newCodeDialog";
import SkeletonComponent from "../../../../components/Skeleton/SkeletonComponent";

const CodeListView = ({
  state,
  role,
  tourCodes,
  classes,
  tourId,
  handleOpenAddCodeDialog,
  handleCloseAddCodeDialog,
  dialogOpen,
  handleSearchChange,
  formatDate,
  getStatut,
  isMailSent,
  sendMail,
  searchTerm,
  archiveTourCode,
  formatDateShort,
  handleChangePage,
  handleChangeRowsPerPage,
  onOpenStats,
  tourDisabled,
  userLanguage,
}) => {
  const { t } = useTranslation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  const handleOpenDetails = (code) => {
    console.log("s code", code);
    setSelectedCode(code);
    setDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedCode(null);
  };

  const getStatusClass = (status) => {
    switch (status) {
      case t("code_list.used"):
        return classes.statusActive;
      case t("code_list.expired"):
        return classes.statusExpired;
      default:
        return classes.statusPending;
    }
  };

  return (
    <>
      <Box mb={3} display="flex" justifyContent="center">
        <TextField
          fullWidth
          variant="outlined"
          placeholder={t("code_list.search")}
          value={searchTerm}
          onChange={handleSearchChange}
          className={classes.searchInput}
        />
      </Box>

      {tourDisabled && (
        <Box className={classes.tourDeactivated}>
          {t("code_list.tour_deactivated")}
        </Box>
      )}

      <TableContainer component={Paper} className={classes.table}>
        {state.showSkeletonCodes ? (
          <SkeletonComponent type="lines"></SkeletonComponent>
        ) : (
          <>
            <Table className={classes.codesTable} sx={{ minWidth: 650 }} aria-label="">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableCellHeader}>
                    {t("code_list.name")}
                  </TableCell>
                  <TableCell className={classes.tableCellHeader}>
                    {t("code_list.code")}
                  </TableCell>
                  <TableCell className={classes.tableCellHeader}>
                    {t("code_list.status")}
                  </TableCell>
                  <TableCell
                    className={classes.tableCellHeader}
                    align="center"
                    style={{ width: 50 }}
                  >
                    {t("code_list.send")}
                  </TableCell>
                  <TableCell
                    className={classes.tableCellHeader}
                    align="center"
                    style={{ width: 50 }}
                  >
                    {t("code_list.details")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tourCodes?.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className={classes.tableCell}>
                      <Box variant="body2" style={{ fontWeight: 500 }}>
                        {code.guestGameUser.teamName && (
                          <div
                            style={{ fontStyle: "italic", marginBottom: "2px" }}
                          >
                            {code.guestGameUser.teamName}
                          </div>
                        )}
                        <Box display="flex" alignItems="flex-start" gap={2}>
                          <Box>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 500 }}
                            >
                              {code.guestGameUser.firstname}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ fontWeight: 400 }}
                            >
                              {code.guestGameUser.lastname}
                            </Typography>
                          </Box>
                          <Box>
                            {code.isTest && (
                              <Box
                                style={{
                                  border: "1px solid #768FDD",
                                  borderRadius: "4px",
                                  padding: "2px 6px",
                                  marginLeft: "4px",
                                }}
                              >
                                <Typography
                                  style={{ color: "#768FDD", fontSize: "11px" }}
                                >
                                  TEST
                                </Typography>
                              </Box>
                            )}
                            {code.unlimitedUsages && (
                              <Box
                                style={{
                                  border: "1px solid #768FDD",
                                  borderRadius: "4px",
                                  padding: "2px 6px",
                                  marginLeft: "4px",
                                  marginTop: "4px",
                                }}
                              >
                                <Typography
                                  style={{ color: "#768FDD", fontSize: "11px" }}
                                >
                                  {t("code_list.unlimited")}
                                </Typography>
                              </Box>
                            )}
                            {code.createdFromStripeConnectBuy && (
                              <Box
                                style={{
                                  border: "1px solid #768FDD",
                                  borderRadius: "4px",
                                  padding: "2px 6px",
                                  marginLeft: "4px",
                                  marginTop: "4px",
                                }}
                              >
                                <Typography
                                  style={{ color: "#768FDD", fontSize: "11px" }}
                                >
                                  Stripe
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Typography
                        variant="body2"
                        style={{
                          fontFamily: "monospace",
                          letterSpacing: "1px",
                        }}
                      >
                        {code.code}
                      </Typography>
                    </TableCell>
                    <TableCell className={classes.tableCell}>
                      <Box
                        className={`${classes.statusBadge} ${getStatusClass(
                          getStatut(code.endDate, code.usageDate, code.isTest)
                        )}`}
                      >
                        {getStatut(code.endDate, code.usageDate, code.isTest)}
                      </Box>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      <Button
                        variant="contained"
                        className={
                          isMailSent(code)
                            ? classes.alreadySentMailButton
                            : classes.sendMailButton
                        }
                        onClick={() => sendMail(code.id)}
                        startIcon={<SendIcon />}
                        disabled={!code.guestGameUser?.email}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <Typography
                            style={{ fontWeight: 600, fontSize: "12px" }}
                          >
                            {isMailSent(code)
                              ? t("code_list.resend")
                              : t("code_list.send")}
                          </Typography>
                          {isMailSent(code) && (
                            <Typography
                              style={{ fontSize: "10px", opacity: 0.8 }}
                            >
                              {formatDateShort(code.sentAt)}
                            </Typography>
                          )}
                        </Box>
                      </Button>
                    </TableCell>
                    <TableCell className={classes.tableCell} align="center">
                      <IconButton
                        className={classes.detailsButton}
                        onClick={() => handleOpenDetails(code)}
                        size="small"
                      >
                        <VisibilityIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={state.totalRows}
              rowsPerPage={state.rowPerPage}
              page={state.page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              className={classes.pagination}
              labelRowsPerPage={t("code_list.rows_per_page")}
              labelDisplayedRows={({ from, to, count }) => {
                `${from}-${to} ${t("code_list.of")} ${count}`;
              }}
              style={{ color: "white" }}
            />
          </>
        )}
      </TableContainer>

      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            backgroundColor: "#2A3142",
            borderRadius: "20px",
            padding: "20px",
          },
        }}
        BackdropProps={{
          style: {
            backgroundColor: "rgba(0, 0, 0, 0.7)",
          },
        }}
      >
        <DialogTitle>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" style={{ color: "white" }}>
              {t("code_list.details_title")}
            </Typography>
            <IconButton onClick={handleCloseDetails} style={{ color: "white" }}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedCode && (
            <Box>
              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.name")}
                </Typography>
                <Box variant="body2" style={{ color: "white" }}>
                  {selectedCode.guestGameUser.teamName && (
                    <div style={{ fontStyle: "italic", marginBottom: "4px" }}>
                      {selectedCode.guestGameUser.teamName}
                    </div>
                  )}
                  {selectedCode.guestGameUser.firstname}{" "}
                  {selectedCode.guestGameUser.lastname}
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.contact")}
                </Typography>
                <Box variant="body2" style={{ color: "white" }}>
                  {selectedCode.guestGameUser.email}
                </Box>
                <Box variant="body2" style={{ color: "white" }}>
                  {selectedCode.guestGameUser.phoneNumber}
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.information")}
                </Typography>
                <Box display="flex" gap={1} mt={0.5}>
                  {selectedCode.isTest && (
                    <Box
                      style={{
                        border: "1px solid #768FDD",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      <Typography
                        style={{ color: "#768FDD", fontSize: "11px" }}
                      >
                        TEST
                      </Typography>
                    </Box>
                  )}
                  {selectedCode.guestGameUser.language && (
                    <Box
                      style={{
                        border: "1px solid #768FDD",
                        borderRadius: "4px",
                        padding: "2px 6px",
                      }}
                    >
                      <Typography
                        style={{ color: "#768FDD", fontSize: "11px" }}
                      >
                        {selectedCode.guestGameUser?.language?.startsWith("fr")
                          ? t("new_code_dialog.languages.fr")
                          : t("new_code_dialog.languages.en")}
                      </Typography>
                    </Box>
                  )}
                  {selectedCode.unlimitedUsages && (
                    <Box
                      style={{
                        border: "1px solid #768FDD",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        marginLeft: "4px",
                      }}
                    >
                      <Typography
                        style={{ color: "#768FDD", fontSize: "11px" }}
                      >
                        {t("code_list.unlimited")}
                      </Typography>
                    </Box>
                  )}
                  {selectedCode.createdFromStripeConnectBuy && (
                    <Box
                      style={{
                        border: "1px solid #768FDD",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        marginLeft: "4px",
                      }}
                    >
                      <Typography
                        style={{ color: "#768FDD", fontSize: "11px" }}
                      >
                        Stripe
                      </Typography>
                    </Box>
                  )}
                  {selectedCode.isStoreGame && (
                    <Box
                      style={{
                        border: "1px solid #768FDD",
                        borderRadius: "4px",
                        padding: "2px 6px",
                        marginLeft: "4px",
                      }}
                    >
                      <Typography
                        style={{ color: "#768FDD", fontSize: "11px" }}
                      >
                        Store
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.code")}
                </Typography>
                <Typography
                  variant="body2"
                  style={{
                    color: "white",
                    fontFamily: "monospace",
                    letterSpacing: "1px",
                    fontSize: "14px",
                    backgroundColor: "rgba(118, 143, 221, 0.1)",
                    padding: "8px",
                    borderRadius: "4px",
                  }}
                >
                  {selectedCode.code}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.expiration_date")}
                </Typography>
                <Typography variant="body2" style={{ color: "white" }}>
                  {formatDate(selectedCode.endDate)}
                </Typography>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.status")}
                </Typography>
                <Box
                  className={`${classes.statusBadge} ${getStatusClass(
                    getStatut(
                      selectedCode.endDate,
                      selectedCode.usageDate,
                      selectedCode.isTest
                    )
                  )}`}
                >
                  {getStatut(
                    selectedCode.endDate,
                    selectedCode.usageDate,
                    selectedCode.isTest
                  )}
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" style={{ color: "#768FDD" }}>
                  {t("code_list.created_at")}
                </Typography>
                <Typography variant="body2" style={{ color: "white" }}>
                  {formatDate(selectedCode.createdAt)}
                </Typography>
              </Box>

              <Box mt={3} display="flex" justifyContent="flex-end">
                <Button
                  className={classes.archiveButton}
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    archiveTourCode(selectedCode.id);
                    handleCloseDetails();
                  }}
                  style={{
                    backgroundColor: "#FF4B4B",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#FF3333",
                    },
                  }}
                >
                  {t("code_list.archive")}
                </Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <NewCodeDialog
        open={dialogOpen}
        handleClose={handleCloseAddCodeDialog}
        tourId={tourId}
        userLanguage={userLanguage}
      />
    </>
  );
};

export default withStyles(styles)(CodeListView);
